import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../LanguageContext';
import './Register.css';

function Register() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  const { t } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    setError('');
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError(t('register.errorRequiredFields'));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError(t('register.errorInvalidEmail'));
      return false;
    }
    if (formData.password.length < 8) {
      setError(t('register.errorWeakPassword'));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('register.errorPasswordMismatch'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setError('');

    try {
      await register({ email: formData.email, password: formData.password });
      // After signup the customer is 'incomplete' and is prompted to fill KYC.
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-lang-toggle">
          <LanguageToggle />
        </div>
        <div className="auth-logo">
          <h1>{t('common.appName')}</h1>
          <p>{t('register.subtitle')}</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          {error && (
            <div className="alert alert-error">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          <div className="form-section">
            <div className="form-section-title">{t('register.sectionTitle')}</div>
            <p className="form-description">
              {t('register.sectionDescription')}
            </p>

            <div className="form-field">
              <label htmlFor="email">{t('register.emailLabel')} <span className="required">*</span></label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder={t('register.emailPlaceholder')}
                autoComplete="email"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="password">{t('register.passwordLabel')} <span className="required">*</span></label>
                <input
                  type="password"
                  id="password"
                  value={formData.password}
                  onChange={(e) => updateField('password', e.target.value)}
                  placeholder={t('register.passwordPlaceholder')}
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="form-field">
                <label htmlFor="confirmPassword">{t('register.confirmPasswordLabel')} <span className="required">*</span></label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={(e) => updateField('confirmPassword', e.target.value)}
                  placeholder={t('register.confirmPasswordPlaceholder')}
                  autoComplete="new-password"
                  required
                />
              </div>
            </div>

            <div className="password-requirements">
              <p>{t('register.passwordRequirementsTitle')}</p>
              <ul>
                <li className={formData.password.length >= 8 ? 'valid' : ''}>
                  {t('register.passwordRequirementMinLength')}
                </li>
              </ul>
            </div>
          </div>

          <div className="form-buttons">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('register.submitting') : t('register.submit')}
            </button>
          </div>
        </form>

        <div className="auth-links">
          <p>
            {t('register.haveAccount')}{' '}
            <Link to="/login">{t('register.signIn')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
